import { parsePhoneNumberFromString, CountryCode, formatIncompletePhoneNumber, PhoneNumber } from 'libphonenumber-js';

export interface SupportedCountry {
  code: string;
  label: string;
}

export const supportedCountries: SupportedCountry[] = [
  { code: 'IL', label: 'ישראל' },
  { code: 'US', label: 'ארה"ב' }
];

export interface NormalizedPhone {
  e164: string;
  country?: string;
  /** Country dial code with + (e.g. '+972') */
  countryDialCode: string;
  /** National number without leading zero (e.g. '501234567') */
  nationalNumber: string;
  /** WhatsApp number with country code and national number (e.g. '972501234567') */
  whatsapp?: string;
}

/**
 * Normalizes a phone number to E.164 format
 * @param phone The phone number to normalize
 * @param country The country code (e.g., 'IL', 'US')
 * @returns The normalized phone number in E.164 format, country, countryDialCode and nationalNumber, or null if invalid
 */
export function normalizePhone(phone: string, country?: string): NormalizedPhone | null {
  try {
    if (!phone || typeof phone !== 'string') {
      return null;
    }

    // Clean the phone number: remove apostrophes, spaces, and other non-digit characters except +
    // But preserve leading + for international numbers
    let cleanedPhone = phone.trim();

    // Remove all non-digit characters except + at the start
    // This handles cases like '0535277044' or ' 0535277044' or '053-527-7044'
    if (cleanedPhone.startsWith('+')) {
      // For international numbers, keep + and digits
      cleanedPhone = '+' + cleanedPhone.substring(1).replace(/\D/g, '');
    } else {
      // For local numbers, keep only digits
      cleanedPhone = cleanedPhone.replace(/\D/g, '');
    }

    if (!cleanedPhone || cleanedPhone.length === 0) {
      return null;
    }

    let phoneNumber: PhoneNumber | undefined;

    // If no country is provided and the number starts with 0, try IL as default (Israeli local format)
    if (country) {
      phoneNumber = parsePhoneNumberFromString(cleanedPhone, country as CountryCode);
    } else if (cleanedPhone.startsWith('0') && !cleanedPhone.startsWith('+')) {
      // Israeli local format (starts with 0), try IL first
      phoneNumber = parsePhoneNumberFromString(cleanedPhone, 'IL');
    }

    if (!phoneNumber) {
      phoneNumber = parsePhoneNumberFromString(cleanedPhone);
    }

    if (!phoneNumber?.isValid()) {
      return null;
    }
    const countryCallingCode = phoneNumber.countryCallingCode;
    return {
      e164: phoneNumber.number,
      country: phoneNumber.country || country,
      countryDialCode: `+${countryCallingCode}`,
      nationalNumber: phoneNumber.nationalNumber,
      whatsapp: `${countryCallingCode}${phoneNumber.nationalNumber}`
    };
  } catch {
    return null;
  }
}

/**
 * Formats a phone number in national format
 * @param phone The phone number to format (can be E.164 or national format)
 * @param country The country code (e.g., 'IL', 'US')
 * @returns The formatted phone number in national format, or null if invalid
 */
export function formatNational(phone: string, country?: string): string | null {
  if (!phone || !/\d/.test(phone)) {
    return null;
  }

  try {
    // Try to parse as a complete number first
    const phoneNumber = parsePhoneNumberFromString(phone, country as CountryCode);
    if (phoneNumber?.isValid()) {
      return phoneNumber.formatNational();
    }

    // If parsing fails or number is invalid, try to format as incomplete
    const formatted = formatIncompletePhoneNumber(phone, country as CountryCode);
    if (formatted && /\d/.test(formatted)) {
      return formatted;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Displays a phone number according to the following rule:
 * - If the number is Israeli (country IL), display in national format (e.g., 050-1234567)
 * - For all other countries, keep the international E.164 format with '+' and DO NOT convert
 * - If the number already looks local (starts with '0'), return as-is
 * @param phone The phone number to display
 * @param country Optional ISO country code hint (e.g., 'IL', 'US')
 */
export function displayPhoneNumber(phone: string, country?: string): string {
  if (!phone) return '';

  // If the number is already in a local-looking format, keep it
  if (phone.startsWith('0')) {
    return phone;
  }

  try {
    const normalized = normalizePhone(phone, country);
    if (!normalized) {
      return phone;
    }

    const detectedCountry = (country || normalized.country || '').toUpperCase();

    // Only convert Israeli numbers to national. Others remain with '+' format
    if (detectedCountry === 'IL') {
      const formatted = formatNational(normalized.e164, 'IL');
      return formatted || phone;
    }

    // Non-IL: keep E.164 with '+' and prefix LRM so '+' appears at start in RTL
    const LEFT_TO_RIGHT_MARK = '\u200E';
    return `${LEFT_TO_RIGHT_MARK}${normalized.e164}`;
  } catch {
    return phone;
  }
}

/**
 * Compares two phone numbers to check if they are the same
 * @param phoneA First phone number
 * @param phoneB Second phone number
 * @returns True if the numbers are the same after normalization
 */
export function isSameNumber(phoneA: string, phoneB: string): boolean {
  try {
    const normalizedA = normalizePhone(phoneA, 'IL') || normalizePhone(phoneA, 'US');
    const normalizedB = normalizePhone(phoneB, 'IL') || normalizePhone(phoneB, 'US');

    // Only return true if both numbers are valid and match
    if (!normalizedA || !normalizedB) {
      return false;
    }
    return normalizedA.e164 === normalizedB.e164;
  } catch {
    return false;
  }
}


export const buildPhoneVariants = (phone: string): string[] => {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return [trimmed];

  let e164NoPlus: string;
  let local: string;
  if (digits.startsWith('972')) {
    e164NoPlus = digits;
    local = '0' + digits.substring(3);
  } else if (digits.startsWith('0')) {
    e164NoPlus = '972' + digits.substring(1);
    local = digits;
  } else {
    return Array.from(new Set([trimmed, digits]));
  }
  return Array.from(new Set([trimmed, `+${e164NoPlus}`, e164NoPlus, local]));
};
